import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { useSession } from "../../session/SessionContext";
import styles from "./PortalNav.module.css";
import { Tooltip } from "../shared/Tooltip";
import { useAssignedRequestCount } from "../../hooks/useAssignedRequestCount";

const OrgConsumptionPermission = "consumption.read.org";
const OthersConsumptionPermission = "consumption.read.others";
const RolesAdministerPermission = "roles.administer";
const EntitlementAdministerPermission = "entitlement.administer";
const AccountsAdministerPermission = "accounts.administer";
// Only the permission to see every request opens the Requests screen; an approver without it works from My Requests.
const RequestApprovePermission = "request.approve.any";
// The permission to decide requests assigned to oneself, which My Requests lists and its tab counts.
const RequestDecidePermission = "request.approve";
const AuditReadPermission = "audit.read";
const SettingsWritePermission = "settings.write";
const ConfigurationPermission = "configuration.import-export";
const BedrockModelsManagePermission = "bedrock.models.manage";
const ProvidersAdministerPermission = "providers.administer";

/** One top-level tab: where it goes, its name, and the icon's path data for the narrow-screen icon row. */
interface NavEntry {
  to: string;
  label: string;
  icon: React.ReactNode;
  /** Matches only the exact path, for the root tab. */
  end?: boolean;
  /** Overrides the router's own match, for a tab that stands for a whole section. */
  active?: boolean;
  /** A count shown on the tab to draw attention to what is waiting, when above zero. */
  badge?: number;
}

function getDefaultAdminRoute(permissions: readonly string[]): string {
  const routeByPermission: readonly [string, string][] = [
    [RequestApprovePermission, "/admin/requests"],
    [RolesAdministerPermission, "/admin/roles"],
    [EntitlementAdministerPermission, "/admin/users"],
    [AccountsAdministerPermission, "/admin/system/accounts"],
    [AuditReadPermission, "/admin/system/audit-trail"],
    // Without audit.read, /admin/system opens the first tab the person holds.
    [SettingsWritePermission, "/admin/system"],
    [ConfigurationPermission, "/admin/system"],
    // The Providers screen opens its first tab the person holds.
    [ProvidersAdministerPermission, "/admin/providers"],
    [BedrockModelsManagePermission, "/admin/providers"],
  ];

  for (const [permission, route] of routeByPermission) {
    if (permissions.includes(permission)) {
      return route;
    }
  }

  return "/admin/sync-status";
}

/**
 * The top-level portal tabs shown in the header. They are intentionally limited to the three major
 * destinations: consumption, organisation, and administration.
 */
export function PortalNav({
    allowCollapse = true,
    allowMenu = true,
    onSlackChange,
    onIconSlackChange,
}: {
    /** When false the tabs are never collapsed (the header first frees space by other means). */
    allowCollapse?: boolean;
    /** When false the icons never give way to the menu (the header first frees space by other means). */
    allowMenu?: boolean;
    /** Reports the spare width beside the tabs (negative when they overflow), or null when no tabs are shown. */
    onSlackChange?: (slack: number | null) => void;
    /** Reports the spare width beside the row of icon tabs, as `onSlackChange` does for the worded tabs. */
    onIconSlackChange?: (slack: number | null) => void;
} = {}): React.JSX.Element | null {
    const { t } = useTranslation();
    const { snapshot } = useSession();
    const location = useLocation();
    const rootRef = useRef<HTMLDivElement>(null);
    const navRef = useRef<HTMLElement>(null);
    const iconNavRef = useRef<HTMLElement>(null);
    const [slack, setSlack] = useState(0);
    const [iconSlack, setIconSlack] = useState(0);
    const collapsed = allowCollapse && slack < 0;
    const [menuOpen, setMenuOpen] = useState(false);
    const signedIn = snapshot.status === "signed-in";
    const assignedCount = useAssignedRequestCount(snapshot.status === "signed-in" && snapshot.permissions.includes(RequestDecidePermission));

    useLayoutEffect(() => {
       const root = rootRef.current;
       const nav = navRef.current;
       const iconNav = iconNavRef.current;
       if (!root || !nav || !iconNav) return;
       // Both rows stay mounted (hidden when not in use) so their natural widths can always be measured.
       const measure = () => {
          setSlack(root.clientWidth - nav.scrollWidth);
          setIconSlack(root.clientWidth - iconNav.scrollWidth);
       };
       measure();
       if (typeof ResizeObserver === "undefined") return;
       const observer = new ResizeObserver(measure);
       observer.observe(root);
       observer.observe(nav);
       observer.observe(iconNav);
       return () => observer.disconnect();
    }, [signedIn, snapshot]);

    useLayoutEffect(() => {
       onSlackChange?.(signedIn ? slack : null);
    }, [signedIn, slack, onSlackChange]);

    useLayoutEffect(() => {
       onIconSlackChange?.(signedIn ? iconSlack : null);
    }, [signedIn, iconSlack, onIconSlackChange]);

    useEffect(() => { setMenuOpen(false); }, [location.pathname]);

    useEffect(() => {
       if (!menuOpen) return;
       const onPointerDown = (event: MouseEvent) => {
          if (rootRef.current && !rootRef.current.contains(event.target as Node)) setMenuOpen(false);
       };
       const onKeyDown = (event: KeyboardEvent) => {
          if (event.key === "Escape") setMenuOpen(false);
       };
       document.addEventListener("mousedown", onPointerDown);
       document.addEventListener("keydown", onKeyDown);
       return () => {
          document.removeEventListener("mousedown", onPointerDown);
          document.removeEventListener("keydown", onKeyDown);
       };
    }, [menuOpen]);

    useEffect(() => { if (!collapsed) setMenuOpen(false); }, [collapsed]);

    if (snapshot.status !== "signed-in") {
       return null;
    }

    const permissions = snapshot.permissions;
    const hasOrg = permissions.includes(OrgConsumptionPermission);
    const hasOthers = permissions.includes(OthersConsumptionPermission);
    const hasAnyAdmin = [
        EntitlementAdministerPermission,
        RolesAdministerPermission,
        AccountsAdministerPermission,
        AuditReadPermission,
        RequestApprovePermission,
        SettingsWritePermission,
        ConfigurationPermission,
        BedrockModelsManagePermission,
        ProvidersAdministerPermission,
    ].some((permission) => permissions.includes(permission));

    const defaultAdminRoute = getDefaultAdminRoute(permissions);

    // The tabs this person holds, in order. Administration is active on every /admin page, not only the page it links to, so the
    // tab stays highlighted while the sub-navigation changes.
    const entries: NavEntry[] = [
       { to: "/", end: true, label: t("nav.myConsumption"), icon: <path d="M3 3v18h18M7 15l4-4 3 3 5-6" /> },
       { to: "/my-tools", label: t("nav.myTools"), icon: <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-.6-.6-2.4z" /> },
       { to: "/my-requests", label: t("nav.myRequests"), badge: assignedCount, icon: <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M9 12h6M9 16h4" /> },
       ...(hasOthers
          ? [{ to: "/my-groups", label: t("nav.myGroups"), icon: <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /> }]
          : []),
       ...(hasOrg
          ? [{ to: "/organization", label: t("nav.organization"), icon: <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1M14 9h1M9 13h1M14 13h1M10 21v-4h4v4" /> }]
          : []),
       ...(hasAnyAdmin
          ? [{ to: defaultAdminRoute, label: t("nav.administration"), active: location.pathname.startsWith("/admin"), icon: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></> }]
          : []),
    ];
    // Tabs that do not fit as words become a row of icons, which still shows every destination at a glance; only when the icons
    // do not fit either, and the header has nothing left to give up, do they collapse into the menu.
    const iconTabs = collapsed && (iconSlack >= 0 || !allowMenu);

    const renderLinks = (iconsOnly = false): React.JSX.Element => (
       <>
          {entries.map((entry) => {
             const badge = entry.badge ?? 0;
             const waiting = badge > 0 ? t("nav.waitingBadge", "{{count}} waiting for your decision", { count: badge }) : null;
             const named = waiting === null ? entry.label : `${entry.label}, ${waiting}`;
             return (
             <Tooltip key={entry.to} label={iconsOnly ? named : undefined}>
               <NavLink
                  to={entry.to}
                  end={entry.end}
                  className={({ isActive }) =>
                     `${(entry.active ?? isActive) ? styles.linkActive : styles.link} ${iconsOnly ? styles.iconLink : ""}`
                  }
                  aria-label={iconsOnly ? named : undefined}
               >
                  {iconsOnly ? (
                     <svg className={styles.linkIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        {entry.icon}
                     </svg>
                  ) : (
                     entry.label
                  )}
                  {badge > 0 && (
                     <span className={styles.badge}>
                        <span aria-hidden="true">{badge}</span>
                        {!iconsOnly && <span className="visually-hidden">{`, ${waiting ?? ""}`}</span>}
                     </span>
                  )}
               </NavLink>
             </Tooltip>
             );
          })}
       </>
    );

    return (
        <div className={styles.root} ref={rootRef}>
           <nav
              ref={navRef}
              className={`${styles.nav} ${collapsed ? styles.navMeasuring : ""}`}
              aria-label={t("nav.label")}
              aria-hidden={collapsed ? true : undefined}
           >
              {renderLinks()}
           </nav>
           <nav
              ref={iconNavRef}
              className={`${styles.nav} ${styles.iconNav} ${iconTabs ? "" : styles.navMeasuring}`}
              aria-label={t("nav.label")}
              aria-hidden={iconTabs ? undefined : true}
           >
              {renderLinks(true)}
           </nav>
           {collapsed && !iconTabs && (
              <>
                 <button
                    type="button"
                    className={styles.hamburger}
                    aria-label={t("nav.menu")}
                    aria-haspopup="true"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((open) => !open)}
                 >
                    <svg className={styles.hamburgerIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                       <path d="M3 6h18M3 12h18M3 18h18" />
                    </svg>
                 </button>
                 {menuOpen && (
                    <nav className={styles.menuPanel} aria-label={t("nav.label")}>
                       {renderLinks()}
                    </nav>
                 )}
              </>
           )}
        </div>
    );
 }
